<div class="checkout-container">
  <!-- Header -->
  <div class="checkout-header">
    <h1>Checkout</h1>
    <p class="checkout-subtitle">Complete your order in 3 easy steps</p>
  </div>

  <!-- Progress Indicator -->
  <div class="progress-indicator">
    <div class="step" [class.active]="currentStep() >= 1" [class.completed]="currentStep() > 1">
      <span class="step-number">1</span>
      <span class="step-title">Review Order</span>
    </div>
    <div class="step" [class.active]="currentStep() >= 2" [class.completed]="currentStep() > 2">
      <span class="step-number">2</span>
      <span class="step-title">Delivery Option</span>
    </div>
    <div class="step" [class.active]="currentStep() >= 3" [class.completed]="currentStep() > 3">
      <span class="step-number">3</span>
      <span class="step-title">Payment Method</span>
    </div>
    <div class="step" [class.active]="currentStep() >= 4">
      <span class="step-number">4</span>
      <span class="step-title">Complete Payment</span>
    </div>
  </div>

  <!-- Step 1: Order Review -->
  <div class="checkout-step" *ngIf="currentStep() === 1">
    <div class="step-header">
      <h2>Review Your Order</h2>
      <p>Please review the items in your order before proceeding</p>
    </div>

    <div class="order-summary" *ngIf="products().length > 0">
      <div class="order-items">
        <div class="order-item" *ngFor="let product of products()">
          <img [src]="getProductImage(product)" 
               [alt]="product.productName"
               class="product-image">
          <div class="item-details">
            <h3 class="product-name">{{ product.productName }}</h3>
            <p class="product-quantity">Quantity: {{ product.quantity }}</p>
            <p class="product-price">{{ formatPrice(product.totalPrice) }}</p>
          </div>
        </div>
      </div>

      <div class="order-totals">
        <h3>Order Summary</h3>
        <div class="summary-row">
          <span>Subtotal:</span>
          <span>{{ formatPrice(subtotal()) }}</span>
        </div>
        <div class="summary-row total">
          <span>Total:</span>
          <span>{{ formatPrice(totalAmount()) }}</span>
        </div>
      </div>
    </div>

    <div class="step-actions">
      <button class="btn btn-secondary" (click)="cancelCheckout()">Cancel</button>
      <button class="btn btn-primary" 
              (click)="nextStep()" 
              [disabled]="products().length === 0">
        Continue to Delivery Option
      </button>
    </div>
  </div>

  <!-- Step 2: Pickup Location / Delivery Address Selection -->
  <div class="checkout-step" *ngIf="currentStep() === 2">
    <div class="step-header">
      <h2>Select Delivery Option</h2>
      <p>Choose how you'd like to receive your order</p>
    </div>

    <!-- Delivery Option Toggle -->
    <div class="delivery-option-toggle" *ngIf="hasLogisticsProvider()">
      <button 
        type="button"
        class="option-btn" 
        [class.active]="deliveryOption() === 'pickup'"
        (click)="deliveryOption.set('pickup')">
        <i class="pi pi-map-marker"></i>
        <span>Pickup from Store</span>
      </button>
      <button 
        type="button"
        class="option-btn" 
        [class.active]="deliveryOption() === 'delivery'"
        (click)="selectDeliveryOption()">
        <i class="pi pi-truck"></i>
        <span>Home Delivery</span>
      </button>
    </div>
    
    <!-- Show message if logistics provider not configured -->
    <div class="logistics-unavailable" *ngIf="!hasLogisticsProvider() && !isLoadingLogisticsCheck()">
      <p class="info-message">
        <i class="pi pi-info-circle"></i>
        Delivery service is not available for this platform. Please select a pickup location.
      </p>
    </div>

    <!-- Pickup Location Selection -->
    <div class="pickup-section" *ngIf="deliveryOption() === 'pickup'">
      <h3 class="section-title">Select Pickup Location</h3>
      
      <!-- State Filter -->
      <div class="state-filter" *ngIf="availableStates().length > 0">
        <label for="stateFilter">Filter by State:</label>
        <select id="stateFilter" (change)="filterByState($event)">
          <option value="">All States</option>
          <option *ngFor="let state of availableStates()" [value]="state">{{ state }}</option>
        </select>
      </div>

      <!-- Loading State -->
      <div class="loading-container" *ngIf="isLoadingLocations()">
        <p-progressSpinner [style]="{width: '50px', height: '50px'}" strokeWidth="4"></p-progressSpinner>
        <p>Loading pickup locations...</p>
      </div>

      <!-- Pickup Locations -->
      <div class="pickup-locations" *ngIf="!isLoadingLocations()">
        <div class="location-card" 
             *ngFor="let location of pickupLocations()" 
             [class.selected]="selectedLocation()?.storeLocationId === location.storeLocationId"
             (click)="selectPickupLocation(location)">
          <div class="location-info">
            <h3 class="location-name">{{ location.name }}</h3>
            <p class="location-address">{{ location.address }}</p>
            <p class="location-state">{{ location.state }}, {{ location.country }}</p>
          </div>
          <div class="radio-indicator">
            <input type="radio" 
                   [checked]="selectedLocation()?.storeLocationId === location.storeLocationId"
                   [name]="'location'"
                   readonly>
          </div>
        </div>
      </div>

      <!-- No locations message -->
      <div class="no-locations" *ngIf="!isLoadingLocations() && pickupLocations().length === 0">
        <p>No pickup locations available at the moment. Please try again later.</p>
      </div>
    </div>

    <!-- Delivery Address: State, City, Street -->
    <div class="delivery-section" *ngIf="deliveryOption() === 'delivery'">
      <h3 class="section-title">Enter Delivery Address</h3>

      <!-- Saved addresses: pop out addresses user has used before (same as in DB/Terminal to avoid duplicates) -->
      <div class="saved-addresses-section" *ngIf="!isLoadingSavedAddresses() && savedDeliveryAddresses().length > 0">
        <h4 class="saved-addresses-title">Your saved addresses</h4>
        <div class="saved-addresses-list">
          <div 
            class="saved-address-card" 
            *ngFor="let addr of savedDeliveryAddresses()"
            [class.selected]="selectedSavedAddress()?.providerAddressId === addr.providerAddressId"
            (click)="selectSavedAddress(addr)">
            <div class="saved-addr-content">
              <p class="saved-addr-line">{{ addr.line1 }}</p>
              <p class="saved-addr-city">{{ addr.city }}, {{ addr.state }}, {{ addr.country }}</p>
            </div>
            <div class="saved-addr-actions" (click)="$event.stopPropagation()">
              <button type="button" class="btn-edit-address" (click)="openEditAddress(addr, $event)" title="Edit address">Edit</button>
              <button type="button" class="btn-delete-address" (click)="deleteAddress(addr, $event)" [disabled]="isDeletingAddress()" title="Remove address">Delete</button>
            </div>
          </div>
        </div>
        <p class="saved-addresses-hint">Select an address above to use it, or enter a new one below. You can Edit or Delete saved addresses.</p>
      </div>

      <!-- Edit address modal -->
      <div class="edit-address-overlay" *ngIf="editingAddress() && editAddressFormModel" (click)="closeEditAddress()">
        <div class="edit-address-modal" (click)="$event.stopPropagation()">
          <h3>Update delivery address</h3>
          <p class="edit-address-note">Changes are saved to Terminal Africa and your Arkifi saved addresses.</p>
          <div class="edit-address-form">
            <label>Street address *</label>
            <input type="text" [(ngModel)]="editAddressFormModel.line1" placeholder="Line 1" class="address-input" />
            <input type="text" [(ngModel)]="editAddressFormModel.line2" placeholder="Line 2 (optional)" class="address-input" />
            <label>City *</label>
            <input type="text" [(ngModel)]="editAddressFormModel.city" placeholder="City" class="address-input" />
            <label>State *</label>
            <input type="text" [(ngModel)]="editAddressFormModel.state" placeholder="State" class="address-input" />
            <label>Country</label>
            <input type="text" [(ngModel)]="editAddressFormModel.country" placeholder="NG" class="address-input" maxlength="2" />
            <label>Zip (optional)</label>
            <input type="text" [(ngModel)]="editAddressFormModel.zip" placeholder="Zip" class="address-input" />
            <label>First name</label>
            <input type="text" [(ngModel)]="editAddressFormModel.firstName" placeholder="First name" class="address-input" />
            <label>Last name</label>
            <input type="text" [(ngModel)]="editAddressFormModel.lastName" placeholder="Last name" class="address-input" />
            <label>Email</label>
            <input type="email" [(ngModel)]="editAddressFormModel.email" placeholder="Email" class="address-input" />
            <label>Phone</label>
            <input type="text" [(ngModel)]="editAddressFormModel.phone" placeholder="Phone" class="address-input" />
          </div>
          <div class="edit-address-actions">
            <button type="button" class="btn btn-secondary" (click)="closeEditAddress()">Cancel</button>
            <button type="button" class="btn btn-primary" (click)="saveEditedAddress()" [disabled]="isSavingAddress()">{{ isSavingAddress() ? 'Saving...' : 'Save changes' }}</button>
          </div>
        </div>
      </div>
      <div class="loading-inline" *ngIf="isLoadingSavedAddresses()">Loading saved addresses...</div>

      <!-- Delivering to: show the address in use -->
      <div class="delivering-to-summary" *ngIf="getFullDeliveryAddress().length >= 10 || selectedSavedAddress()">
        <strong>Delivering to:</strong>
        <span *ngIf="getFullDeliveryAddress().length >= 10">{{ getFullDeliveryAddress() }}</span>
        <span *ngIf="getFullDeliveryAddress().length < 10 && selectedSavedAddress()">
          {{ selectedSavedAddress()!.line1 }}, {{ selectedSavedAddress()!.city }}, {{ selectedSavedAddress()!.state }}, Nigeria
        </span>
      </div>

      <div class="delivery-address-form">
        <label for="deliveryState">State *</label>
        <select 
          id="deliveryState"
          class="address-select"
          [ngModel]="selectedDeliveryState()?.isoCode"
          (ngModelChange)="onDeliveryStateChange(getDeliveryStateByIsoCode($event))">
          <option value="">Select state</option>
          <option *ngFor="let s of deliveryStates()" [value]="s.isoCode">{{ s.name }}</option>
        </select>
        <div class="loading-inline" *ngIf="isLoadingDeliveryStates()">Loading states...</div>

        <label for="deliveryCity" style="margin-top: 1rem;">City *</label>
        <select 
          id="deliveryCity"
          class="address-select"
          [disabled]="!selectedDeliveryState() || isLoadingDeliveryCities()"
          [ngModel]="selectedDeliveryCity()?.name"
          (ngModelChange)="onDeliveryCityChange(getDeliveryCityByName($event))">
          <option value="">Select city</option>
          <option *ngFor="let c of deliveryCities()" [value]="c.name">{{ c.name }}</option>
        </select>
        <div class="loading-inline" *ngIf="isLoadingDeliveryCities()">Loading cities...</div>

        <label for="deliveryAddressLine" style="margin-top: 1rem;">Street address *</label>
        <textarea 
          id="deliveryAddressLine"
          [ngModel]="deliveryAddressLine()"
          (ngModelChange)="deliveryAddressLine.set($event); onDeliveryAddressLineChange()"
          placeholder="Street, building, area"
          rows="3"
          class="address-input"
          [class.invalid]="deliveryAddressLine().trim().length > 0 && deliveryAddressLine().trim().length < 10"
        ></textarea>
        <small class="form-hint" *ngIf="deliveryAddressLine().trim().length > 0 && deliveryAddressLine().trim().length < 10">
          Street address must be at least 10 characters
        </small>
      </div>

      <!-- Store Selection for Delivery (Ship From Location) -->
      <h3 class="section-title" style="margin-top: 1.5rem;">Select Store to Ship From</h3>
      
      <!-- State Filter -->
      <div class="state-filter" *ngIf="availableStates().length > 0">
        <label for="stateFilterDelivery">Filter by State:</label>
        <select id="stateFilterDelivery" (change)="filterByState($event)">
          <option value="">All States</option>
          <option *ngFor="let state of availableStates()" [value]="state">{{ state }}</option>
        </select>
      </div>

      <!-- Loading State -->
      <div class="loading-container" *ngIf="isLoadingLocations()">
        <p-progressSpinner [style]="{width: '50px', height: '50px'}" strokeWidth="4"></p-progressSpinner>
        <p>Loading store locations...</p>
      </div>

      <!-- Store Locations for Delivery -->
      <div class="pickup-locations" *ngIf="!isLoadingLocations()">
        <div class="location-card" 
             *ngFor="let location of pickupLocations()" 
             [class.selected]="selectedLocation()?.storeLocationId === location.storeLocationId"
             (click)="selectPickupLocation(location)">
          <div class="location-info">
            <h3 class="location-name">{{ location.name }}</h3>
            <p class="location-address">{{ location.address }}</p>
            <p class="location-state">{{ location.state }}, {{ location.country }}</p>
          </div>
          <div class="radio-indicator">
            <input type="radio" 
                   [checked]="selectedLocation()?.storeLocationId === location.storeLocationId"
                   [name]="'deliveryLocation'"
                   readonly>
          </div>
        </div>
      </div>

      <!-- No locations message -->
      <div class="no-locations" *ngIf="!isLoadingLocations() && pickupLocations().length === 0">
        <p>No store locations available at the moment. Please try again later.</p>
      </div>

      <!-- Delivery Quote Section -->
      <div class="delivery-quote-section" *ngIf="selectedLocation() && getFullDeliveryAddress().length >= 10">
        <h3 class="section-title" style="margin-top: 1.5rem;">Select Delivery Option</h3>
        
        <!-- Loading Quotes -->
        <div class="loading-container" *ngIf="isLoadingQuotes()">
          <p-progressSpinner [style]="{width: '40px', height: '40px'}" strokeWidth="4"></p-progressSpinner>
          <p>Getting delivery quotes...</p>
        </div>

        <!-- Delivery Company Options -->
        <div class="delivery-quotes" *ngIf="!isLoadingQuotes() && deliveryQuotes().length > 0">
          <div class="quote-card" 
               *ngFor="let quote of deliveryQuotes()"
               [class.selected]="selectedDeliveryCompany()?.companyId === quote.companyId"
               (click)="selectDeliveryCompany(quote)">
            <div class="quote-logo" *ngIf="quote.carrierLogo || quote.carrier_logo">
              <img [src]="quote.carrierLogo || quote.carrier_logo" [alt]="quote.companyName" class="carrier-logo" />
            </div>
            <div class="quote-info">
              <h4 class="company-name">{{ quote.companyName }}</h4>
              <p class="delivery-time" *ngIf="quote.estimatedDeliveryTime">
                <i class="pi pi-clock"></i> {{ quote.estimatedDeliveryTime }}
              </p>
            </div>
            <div class="quote-price">
              <span class="price">{{ formatPrice(quote.deliveryFee) }}</span>
            </div>
            <div class="radio-indicator">
              <input type="radio" 
                     [checked]="selectedDeliveryCompany()?.companyId === quote.companyId"
                     [name]="'deliveryCompany'"
                     readonly>
            </div>
          </div>
        </div>

        <!-- No Quotes Available -->
        <div class="no-quotes" *ngIf="!isLoadingQuotes() && deliveryQuotes().length === 0 && selectedLocation()">
          <p class="info-message">
            <i class="pi pi-info-circle"></i>
            No delivery quotes available. Please try a different address or store location.
          </p>
        </div>

        <!-- Delivery Fee Summary -->
        <div class="delivery-fee-summary" *ngIf="selectedDeliveryCompany()">
          <div class="fee-row">
            <span>Delivery Fee ({{ selectedDeliveryCompany().companyName }})</span>
            <span class="fee-amount">{{ formatPrice(deliveryFee()) }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="step-actions">
      <button class="btn btn-secondary" (click)="previousStep()">Back</button>
      <button class="btn btn-primary" 
              (click)="nextStep()" 
              [disabled]="!selectedLocation() || (deliveryOption() === 'delivery' && (getFullDeliveryAddress().length < 10 || !selectedDeliveryState() || !selectedDeliveryCity()))">
        Continue to Payment Method
      </button>
    </div>
  </div>

  <!-- Step 3: Payment Method Selection -->
  <div class="checkout-step" *ngIf="currentStep() === 3">
    <div class="step-header">
      <h2>Choose Payment Method</h2>
      <p>Select how you'd like to pay for your order</p>
    </div>

    <div class="payment-methods">
      <!-- Shopping Card Payment -->
      <div class="payment-option" 
           [class.selected]="selectedPaymentMethod() === PaymentMethod.ShoppingCard"
           [class.disabled]="!canUseShoppingCard()"
           (click)="canUseShoppingCard() ? selectPaymentMethod(PaymentMethod.ShoppingCard) : null">
        <div class="payment-icon">💳</div>
        <div class="payment-details">
          <h3>Arkifi Shopping Card</h3>
          <p>Pay with your prepaid balance</p>
          <p class="balance" *ngIf="!isLoadingBalance()">Balance: {{ getShoppingCardBalance() }}</p>
          <p class="balance" *ngIf="isLoadingBalance()">Loading balance...</p>
          <p class="insufficient-funds" *ngIf="!canUseShoppingCard() && !isLoadingBalance()">
            Insufficient funds for this order
          </p>
        </div>
        <div class="radio-indicator">
          <input type="radio" 
                 [checked]="selectedPaymentMethod() === PaymentMethod.ShoppingCard"
                 [name]="'paymentMethod'"
                 [disabled]="!canUseShoppingCard()"
                 readonly>
        </div>
      </div>

      <!-- Payment Gateway -->
      <div class="payment-option" 
           [class.selected]="selectedPaymentMethod() === PaymentMethod.PaymentGateway"
           (click)="selectPaymentMethod(PaymentMethod.PaymentGateway)">
        <div class="payment-icon">🏦</div>
        <div class="payment-details">
          <h3>Payment Gateway</h3>
          <p>Pay with card, bank transfer, or other methods</p>
          <p class="payment-note">You'll be redirected to a secure payment page</p>
        </div>
        <div class="radio-indicator">
          <input type="radio" 
                 [checked]="selectedPaymentMethod() === PaymentMethod.PaymentGateway"
                 [name]="'paymentMethod'"
                 readonly>
        </div>
      </div>
    </div>

    <!-- Saved Card Selection for Payment Gateway (only when platform supports Paystack/Monnify) -->
    <div class="saved-card-section" *ngIf="supportsSavedCards() && selectedPaymentMethod() === PaymentMethod.PaymentGateway && savedCards().length > 0">
      <div class="section-header">
        <h3>Or use a saved card</h3>
        <p>Select a saved card to charge directly</p>
      </div>
      
      @if (isLoadingCards()) {
        <div class="loading-cards">
          <p-progressSpinner [style]="{ width: '20px', height: '20px' }"></p-progressSpinner>
          <span>Loading cards...</span>
        </div>
      } @else {
        <div class="cards-list">
          @for (card of savedCards(); track card.id) {
            <div
              class="card-item"
              [class.selected]="selectedCardId() === card.id"
              (click)="selectedCardId.set(card.id)"
            >
              <div class="card-info">
                <div class="card-type-badge">{{ card.cardType || 'Card' }}</div>
                <div class="card-number">{{ getMaskedCardNumber(card.last4) }}</div>
                <div class="card-bank">{{ card.bank || 'N/A' }}</div>
              </div>
              <div class="card-check">
                @if (selectedCardId() === card.id) {
                  <i class="pi pi-check-circle"></i>
                } @else {
                  <i class="pi pi-circle"></i>
                }
              </div>
            </div>
          }
        </div>
        <div class="add-card-link">
          <a [routerLink]="['/cards']" [queryParams]="{ platformId: platformId() }">
            <i class="pi pi-plus"></i> Add a new card
          </a>
        </div>
      }
    </div>

    <!-- Transaction PIN Input - Always Required -->
    <div class="pin-input-section" *ngIf="selectedPaymentMethod()">
      <div class="pin-input-container">
        <label for="transactionPin">Transaction PIN:</label>
        <input type="password" 
               id="transactionPin" 
               [(ngModel)]="transactionPin"
               placeholder="Enter your 4-digit PIN"
               maxlength="4"
               class="pin-input"
               [class.error]="transactionPin() && (transactionPin().length !== 4 || !/^\d{4}$/.test(transactionPin()))"
               (input)="transactionPin.set($any($event.target).value)">
        <div class="pin-validation" *ngIf="transactionPin() && (transactionPin().length !== 4 || !/^\d{4}$/.test(transactionPin()))">
          <small class="error-message">PIN must be exactly 4 digits</small>
        </div>
        <p class="pin-help">
          @if (selectedPaymentMethod() === PaymentMethod.ShoppingCard) {
            Enter the 4-digit PIN you set up for your shopping card
          } @else if (supportsSavedCards() && selectedPaymentMethod() === PaymentMethod.PaymentGateway && selectedCardId()) {
            Enter your 4-digit transaction PIN to authorize payment with saved card
          } @else {
            Enter your 4-digit transaction PIN (required for all payments)
          }
        </p>
      </div>
    </div>

    <div class="step-actions">
      <button class="btn btn-secondary" (click)="previousStep()">Back</button>
      <button class="btn btn-primary" 
              (click)="nextStep()" 
              [disabled]="!selectedPaymentMethod() || !transactionPin() || transactionPin().length !== 4 || !/^\d{4}$/.test(transactionPin()) || (selectedPaymentMethod() === PaymentMethod.PaymentGateway && selectedCardId() && !selectedCardId())">
        Continue to Review
      </button>
    </div>
  </div>

  <!-- Step 4: Payment Confirmation -->
  <div class="checkout-step" *ngIf="currentStep() === 4">
    <div class="step-header">
      <h2>Complete Payment</h2>
      <p>Review your order details and proceed to payment</p>
    </div>

    <!-- Order Review Summary -->
    <div class="payment-summary">
      <div class="summary-section">
        <h3>Order Items</h3>
        <div class="item-list">
          <div class="item-row" *ngFor="let product of products()">
            <span class="item-name">{{ product.productName }} (x{{ product.quantity }})</span>
            <span class="item-price">{{ formatPrice(product.totalPrice) }}</span>
          </div>
        </div>
      </div>

      <div class="summary-section">
        <h3>{{ deliveryOption() === 'pickup' ? 'Pickup Location' : 'Delivery Address' }}</h3>
        <div class="location-info" *ngIf="deliveryOption() === 'pickup' && selectedLocation()">
          <p><strong>{{ selectedLocation()?.name }}</strong></p>
          <p>{{ selectedLocation()?.address }}</p>
          <p>{{ selectedLocation()?.state }}, {{ selectedLocation()?.country }}</p>
        </div>
        <div class="location-info" *ngIf="deliveryOption() === 'delivery' && getFullDeliveryAddress()">
          <p>{{ getFullDeliveryAddress() }}</p>
        </div>
      </div>

      <div class="summary-section">
        <h3>Order Total</h3>
        <div class="total-breakdown">
          <div class="breakdown-row">
            <span>Subtotal:</span>
            <span>{{ formatPrice(subtotal()) }}</span>
          </div>
          <div class="breakdown-row" *ngIf="deliveryOption() === 'delivery' && deliveryFee() > 0">
            <span>Delivery Fee ({{ selectedDeliveryCompany()?.companyName }}):</span>
            <span>{{ formatPrice(deliveryFee()) }}</span>
          </div>
          <div class="breakdown-row" *ngIf="deliveryOption() === 'pickup'">
            <span>Delivery Fee:</span>
            <span>Free (Pickup)</span>
          </div>
          <div class="total-row">
            <span><strong>Total Amount:</strong></span>
            <span class="total-amount">{{ formatPrice(totalAmount()) }}</span>
          </div>
        </div>
      </div>

      <div class="payment-note">
        <p><strong>Payment Method:</strong> 
          <span *ngIf="selectedPaymentMethod() === PaymentMethod.ShoppingCard">Arkifi Shopping Card</span>
          <span *ngIf="selectedPaymentMethod() === PaymentMethod.PaymentGateway">Payment Gateway</span>
        </p>
        <p class="note-text" *ngIf="selectedPaymentMethod() === PaymentMethod.PaymentGateway">
          You will be redirected to a secure payment page to complete your order.
        </p>
        <p class="note-text" *ngIf="selectedPaymentMethod() === PaymentMethod.ShoppingCard">
          Your order will be processed immediately using your shopping card balance.
        </p>
      </div>
    </div>

    <div class="step-actions">
      <button class="btn btn-secondary" (click)="previousStep()">Back</button>
      <button class="btn btn-primary btn-lg" 
              (click)="processPayment()" 
              [disabled]="isProcessingPayment()">
        <span *ngIf="!isProcessingPayment()">Proceed to Payment</span>
        <span *ngIf="isProcessingPayment()">Processing...</span>
        <p-progressSpinner *ngIf="isProcessingPayment()" 
                          [style]="{width: '20px', height: '20px'}" 
                          strokeWidth="3"
                          styleClass="inline-spinner"></p-progressSpinner>
      </button>
    </div>
  </div>
</div>

<p-toast position="bottom-right"></p-toast>
